import React, { useState, useEffect, useCallback } from "react";
import { Wallet, IndianRupee, Clock, CheckCircle, TrendingUp, Banknote, Download } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent, PageHeader, StatsCard, StatsGrid, AppBadge, AppButton, EmptyState, Paginator } from "../ui";
import apiClient from "../../utils/apiClient";
import Shimmer from "../Common/Shimmer";
import "./earnings.scss";

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

const TXN_PAGE_SIZE = 25;
const PAYOUT_PAGE_SIZE = 20;

const EarningsPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txnPage, setTxnPage] = useState(1);
  const [txnPagination, setTxnPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [payouts, setPayouts] = useState([]);
  const [payoutPage, setPayoutPage] = useState(1);
  const [payoutPagination, setPayoutPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [txnFilter, setTxnFilter] = useState("all"); // all, pending, settled

  const fetchDashboard = useCallback(async () => {
    try {
      const dashRes = await apiClient.get("/earnings/dashboard");
      setDashboard(dashRes.data.overview || {});
    } catch { /* keep */ }
  }, []);

  const fetchTransactions = useCallback(async (filter = txnFilter, pageArg = txnPage) => {
    try {
      const params = { page: pageArg, limit: TXN_PAGE_SIZE, ...(filter !== "all" ? { payoutStatus: filter } : {}) };
      const { data } = await apiClient.get("/earnings/transactions", { params });
      setTransactions(data.transactions || []);
      setTxnPagination(data.pagination || { page: 1, totalPages: 1, total: (data.transactions || []).length });
    } catch { /* keep */ }
  }, [txnFilter, txnPage]);

  const fetchPayouts = useCallback(async (pageArg = payoutPage) => {
    try {
      const { data } = await apiClient.get("/earnings/payouts", { params: { page: pageArg, limit: PAYOUT_PAGE_SIZE } });
      setPayouts(data.payouts || []);
      setPayoutPagination(data.pagination || { page: 1, totalPages: 1, total: (data.payouts || []).length });
    } catch { /* keep */ }
  }, [payoutPage]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchTransactions("all", 1), fetchPayouts(1)]);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when txn page or filter changes
  useEffect(() => { if (!loading) fetchTransactions(txnFilter, txnPage); }, [txnPage]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!loading) fetchPayouts(payoutPage); }, [payoutPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTxnFilter = (val) => {
    setTxnFilter(val);
    setTxnPage(1);
    fetchTransactions(val, 1);
  };

  if (loading) {
    return <Shimmer height={80} count={5} />;
  }

  const handleExportCsv = async () => {
    try {
      const res = await apiClient.get("/earnings/export-csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `earnings-report-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // silent
    }
  };

  const ov = dashboard || {};

  return (
    <div className="earnings-page">
      <PageHeader
        title="Earnings"
        description="Track your online fee collections and settlements"
        icon={Wallet}
        actions={
          <AppButton variant="outline" size="sm" onClick={handleExportCsv}>
            <Download size={14} className="me-1" />
            Export CSV
          </AppButton>
        }
      />

      {/* Stats */}
      <StatsGrid className="earnings-page__stats">
        <StatsCard title="Total Collected" value={formatCurrency(ov.totalOnlineCollected)} icon={IndianRupee} iconColor="blue" />
        <StatsCard title="Commission Deducted" value={formatCurrency(ov.totalCommissionDeducted)} icon={TrendingUp} iconColor="rose" />
        <StatsCard title="Pending Settlement" value={formatCurrency(ov.pendingSettlement)} icon={Clock} iconColor="amber" />
        <StatsCard title="Total Settled" value={formatCurrency(ov.totalSettled)} icon={CheckCircle} iconColor="emerald" />
      </StatsGrid>

      {/* Manual collection info */}
      {ov.totalManualCollection > 0 && (
        <div className="sa-card">
          <div className="sa-card-body" style={{ fontSize: "0.8125rem", color: "var(--text-secondary)" }}>
            <Banknote size={14} className="me-1" />
            You also have <strong>{formatCurrency(ov.totalManualCollection)}</strong> in manual fee collections ({ov.manualTransactionCount} entries) which are not part of online earnings.
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="transactions">
        <TabsList>
          <TabsTrigger value="transactions">Online Transactions</TabsTrigger>
          <TabsTrigger value="payouts">Payout History</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions">
          <div className="earnings-page__section">
            <div className="d-flex gap-2 mb-3">
              {["all", "pending", "settled"].map((f) => (
                <AppBadge
                  key={f}
                  variant={txnFilter === f ? "default" : "outline"}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleTxnFilter(f)}
                >
                  {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
                </AppBadge>
              ))}
            </div>

            {transactions.length === 0 ? (
              <EmptyState icon={<IndianRupee size={28} />} title="No Transactions" description="No online fee transactions found." />
            ) : (
              <div className="sa-card">
                <div className="sa-card-body" style={{ overflowX: "auto", padding: 0 }}>
                  <table className="earnings-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Amount</th>
                        <th>Commission</th>
                        <th>Net Payable</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((t) => (
                        <tr key={t._id}>
                          <td>
                            <div className="earnings-table__name">{t.studentId?.name || "—"}</div>
                            <div className="earnings-table__sub">{t.studentId?.email}</div>
                          </td>
                          <td className="earnings-table__amount">{formatCurrency(t.amount)}</td>
                          <td className="earnings-table__amount earnings-table__amount--red">{formatCurrency(t.commissionAmount)}</td>
                          <td className="earnings-table__amount earnings-table__amount--green">{formatCurrency(t.netPayable)}</td>
                          <td>
                            <AppBadge
                              variant="outline"
                              color={t.payoutStatus === "settled" ? "emerald" : "amber"}
                            >
                              {t.payoutStatus}
                            </AppBadge>
                          </td>
                          <td style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>{formatDate(t.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <Paginator
              page={txnPagination.page}
              totalPages={txnPagination.totalPages}
              total={txnPagination.total}
              pageSize={TXN_PAGE_SIZE}
              onChange={setTxnPage}
              infoLabel="transactions"
            />
          </div>
        </TabsContent>

        <TabsContent value="payouts">
          <div className="earnings-page__section">
            {payouts.length === 0 ? (
              <EmptyState icon={<CheckCircle size={28} />} title="No Payouts Yet" description="No settlements have been made yet." />
            ) : (
              <div className="sa-card">
                <div className="sa-card-body" style={{ overflowX: "auto", padding: 0 }}>
                  <table className="earnings-table">
                    <thead>
                      <tr>
                        <th>Net Paid</th>
                        <th>Commission</th>
                        <th>Collected</th>
                        <th>Txns</th>
                        <th>Period</th>
                        <th>Method</th>
                        <th>Settled On</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payouts.map((p) => (
                        <tr key={p._id}>
                          <td className="earnings-table__amount earnings-table__amount--green">{formatCurrency(p.netPayable)}</td>
                          <td className="earnings-table__amount">{formatCurrency(p.totalCommission)}</td>
                          <td className="earnings-table__amount">{formatCurrency(p.totalCollected)}</td>
                          <td>{p.transactionCount}</td>
                          <td style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                            {formatDate(p.periodStart)} – {formatDate(p.periodEnd)}
                          </td>
                          <td>
                            <AppBadge variant="secondary">{p.settlementMethod?.replace(/_/g, " ")}</AppBadge>
                          </td>
                          <td style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>{formatDate(p.settledAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <Paginator
              page={payoutPagination.page}
              totalPages={payoutPagination.totalPages}
              total={payoutPagination.total}
              pageSize={PAYOUT_PAGE_SIZE}
              onChange={setPayoutPage}
              infoLabel="payouts"
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EarningsPage;
